import * as Adaptive from "adaptivecards";
import { createRoot } from 'react-dom/client';
import { IDynamicPerson, PeoplePicker, PersonType, UserType } from '@microsoft/mgt-react';
import React from "react";
import { Input } from "adaptivecards";
import { BooleanPropertyEditor, ChoicePropertyEditor, IVersionedChoice, PropertySheet, PropertySheetCategory, StringPropertyEditor, TypedCardElementPeer } from "adaptivecards-designer";
import { handleValidationElement, initCustomRenderedElement, removeOtherPropertyConfigurations } from "../Utils/CustomElementUtils";
import { AdaptiveCardTexts } from "../../../shared/utils/constants";
import MgtCss from '../../../../../src/components/mgt.module.css';
import { AffectedUsersPeoplePicker } from "./AffectedUsersPeoplePicker";
import { RequesterPeoplePicker } from "./RequesterPeoplePicker";
import { searchUsers } from "../../../shared/cache/PlatformUserCache";
import _ from "lodash";
import { graphService } from "../../../shared/services/graph.service";

const displayLabelProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "displayLabel", true);
const selectionModeProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "selectionMode", true);
const displayChoicesProperty = new Adaptive.EnumProperty(Adaptive.Versions.v1_0, "displayChoices", []);
const requireFieldProperty = new Adaptive.BoolProperty(Adaptive.Versions.v1_0, "requireField", false);

export const renderACPeoplePicker = (self: AffectedUsersPeoplePicker | RequesterPeoplePicker | CardPeoplePicker, valueChange: (input: Input) => void, disableField: boolean): HTMLElement => {
  let renderedElement = initCustomRenderedElement();

  const renderElement = (self: AffectedUsersPeoplePicker | RequesterPeoplePicker | CardPeoplePicker, element: HTMLDivElement, valueChange: (input: Input) => void, selectedUsers: IDynamicPerson[], disableField?: boolean) => {
    const onSelectedUser = async (e: any) => {
      const selectedUsers: any[] = e.detail;
      if (selectedUsers.length > 0) {        
        const values = selectedUsers.map(i => { return { FullName: i.displayName, UserName: i.userPrincipalName, AadObjectId: i.id } });
        self.valueField = JSON.stringify(values);

        handleValidationElement(element);
      } else
        self.valueField = undefined;

      valueChange?.(self);
    };

    const domNode = document.createElement('div');
    const root = createRoot(domNode);
    root.render(<PeoplePicker placeholder={AdaptiveCardTexts.SEARCH_NAME} userType={UserType.user} type={PersonType.person} className={`${MgtCss.groupsPicker} tikit-cardPicker`}
      selectionChanged={onSelectedUser} selectionMode={self.selectionMode ?? "multiple"} selectedPeople={selectedUsers} {...disableField && { disabled: true }} />);

    element.append(domNode);
  }

  let selectedUsers = []; let renderAsync = false;
  if (self.valueField && self.valueField.length > 0) {
    try {
      const parsedValue: any[] = JSON.parse(self.valueField);
      selectedUsers = parsedValue.map((i: any) => ({ displayName: i.FullName, userPrincipalName: i.UserName, id: i.AadObjectId }));
    } catch {
      const populateUsers = async (values: string[]) => {
        const graph = new graphService();
        const pUsers = [];
        for (let value of values) {
          let user = await searchUsers({ AadObjectId: value } as PlatformUser, false);
          if (!user) {
            const gu = (await graph.getUser(value))?.data;
            user = gu ? { FullName: gu.displayName, UserName: gu.userPrincipalName, AadObjectId: gu.id } as PlatformUser : undefined;
          }

          pUsers.push(user);
        }

        const selectedUsers = _.compact(pUsers).map((i: any) => ({ displayName: i.FullName, userPrincipalName: i.UserName, id: i.AadObjectId }));
        renderElement(self, renderedElement, valueChange, selectedUsers, disableField);
      };

      const valueStrings = self.valueField.split(",");
      if (valueStrings.length > 0) {
        renderAsync = true;
        populateUsers(valueStrings);
      }
    }
  }

  if (!renderAsync)
    renderElement(self, renderedElement, valueChange, selectedUsers, disableField);

  return renderedElement;
}

export class CardPeoplePicker extends Adaptive.TextInput {
  static readonly displayLabelProperty = displayLabelProperty;
  static readonly selectionModeProperty = selectionModeProperty;
  static readonly displayChoicesProperty = displayChoicesProperty;
  static readonly requireFieldProperty = requireFieldProperty;
  static readonly JsonTypeName = "People Picker";
  static readonly defaultLabel = "People Picker";
  static pickerChoices: any[];
  static onCustomValueChange: (input: Input) => void;
  static disableField: boolean;

  //#region Schema

  @Adaptive.property(displayLabelProperty)
  get displayLabel(): string | undefined {
    return this.getValue(CardPeoplePicker.displayLabelProperty);
  }

  set displayLabel(value: string | undefined) {
    this.setValue(CardPeoplePicker.displayLabelProperty, value);
    this.label = value;
    this.updateLayout();
  }

  @Adaptive.property(selectionModeProperty)
  get selectionMode(): string | undefined {
    return this.getValue(CardPeoplePicker.selectionModeProperty);
  }

  set selectionMode(value: string | undefined,) {
    this.setValue(CardPeoplePicker.selectionModeProperty, value);
    this.updateLayout();
  }

  @Adaptive.property(displayChoicesProperty)
  get displayChoices(): any[] | undefined {
    return this.getValue(CardPeoplePicker.displayChoicesProperty);
  }

  set displayChoices(value: any[] | undefined,) {
    this.setValue(CardPeoplePicker.displayChoicesProperty, value);
    this.updateLayout();
  }

  @Adaptive.property(requireFieldProperty)
  get requireField(): boolean | undefined {
    return this.getValue(CardPeoplePicker.requireFieldProperty);
  }

  set requireField(value: boolean | undefined) {
    this.setValue(CardPeoplePicker.requireFieldProperty, value);
    this.setValue(CardPeoplePicker.isRequiredProperty, value);
    if (value)
      this.setValue(CardPeoplePicker.errorMessageProperty, AdaptiveCardTexts.REQUIRE_FIELD);
    this.updateLayout();
  }

  get valueField(): string | undefined {
    return this.getValue(CardPeoplePicker.valueProperty);
  }

  set valueField(value: string | undefined) {
    this.setValue(CardPeoplePicker.valueProperty, value);
  }

  //#endregion

  protected internalRender(): HTMLElement | undefined {
    if ((this?.displayLabel ?? "").length == 0)
      this.displayLabel = CardPeoplePicker.defaultLabel;

    if (CardPeoplePicker.pickerChoices) 
      this.displayChoices = CardPeoplePicker.pickerChoices;
    
    let selectMode = "multiple";
    if ((this?.selectionMode ?? "").length == 0)
      this.selectionMode = selectMode;
    
    return renderACPeoplePicker(this, CardPeoplePicker.onCustomValueChange, CardPeoplePicker.disableField);
  }

  get value(): string {
    return this.getValue(CardPeoplePicker.valueProperty);
  }

  getJsonTypeName(): string {
    return CardPeoplePicker.JsonTypeName;
  }

  updateLayout(processChildren: boolean = true) {
    super.updateLayout(processChildren);
  }
}

export class CardPeoplePickerPeer extends TypedCardElementPeer<CardPeoplePicker> {
  static readonly displayLabelProperty = new StringPropertyEditor(Adaptive.Versions.v1_0, "displayLabel", "Label", false);
  static readonly selectionModeProperty = new ChoicePropertyEditor(Adaptive.Versions.v1_0, "selectionMode", "Selection", [{ name: 'Single', value: 'single' }, { name: 'Multiple', value: 'multiple' }] as IVersionedChoice[]);
  static requireFieldProperty = new BooleanPropertyEditor(Adaptive.Versions.v1_0, "requireField", "Required", false);

  populatePropertySheet(propertySheet: PropertySheet, defaultCategory: string = PropertySheetCategory.DefaultCategory) {

    super.populatePropertySheet(propertySheet, defaultCategory);
    removeOtherPropertyConfigurations(propertySheet);

    propertySheet.add(
      defaultCategory,
      CardPeoplePickerPeer.idProperty,
      CardPeoplePickerPeer.displayLabelProperty,
      CardPeoplePickerPeer.requireFieldProperty,
      CardPeoplePickerPeer.selectionModeProperty
    );

  }
}

